/**
 * Five cinematic style presets for the Adaptive Reveal Engine.
 *
 * A style is the *director's choice*: which backdrop layers compose the
 * environment, what pacing the buildup uses, what typography scale and
 * audio bed key feel right. Layouts and per-scene tints are applied on top.
 */

import type { CinematicPalette } from '../themePalettes';

export type RevealStyleId =
  | 'ethereal_memory'
  | 'luxury_editorial'
  | 'emotional_film'
  | 'intimate_letter'
  | 'modern_futuristic';

export type BackdropLayerId =
  | 'fog'
  | 'dust'
  | 'ember'
  | 'rain'
  | 'bokeh'
  | 'lightStreaks'
  | 'volumetricRays'
  | 'filmGrain'
  | 'paperTexture'
  | 'holographic'
  | 'aurora';

export type PacingTier = 'slow' | 'cinematic' | 'editorial' | 'intimate' | 'fluid';

export interface RevealStylePreset {
  id: RevealStyleId;
  /** Display name — internal use. */
  label: string;
  /** Backdrop layers to compose (max 5; mobile auto-trims to 3). */
  backdropLayers: BackdropLayerId[];
  /** Buildup pacing in milliseconds total. */
  buildupDurationMs: number;
  /** Reading rhythm hint for Phase B. */
  readingRhythm: 'word' | 'sentence' | 'paragraph';
  /** Pacing tier — multiplies scene timings. */
  pacing: PacingTier;
  /** Audio bed key — resolved by audioBeds map (Phase C). */
  audioBedKey: 'piano_warm' | 'strings_rise' | 'ambient_rain' | 'heartbeat_low' | 'cinematic_air' | 'paper_room_tone';
  /** Hue rotation applied to the palette light/particle for variation. */
  hueShift?: number;
  /** Particle density multiplier (0.5–1.4). */
  particleDensity: number;
  /** Headline font feel hint. */
  typography: 'serif-classic' | 'serif-editorial' | 'sans-modern' | 'serif-handwritten' | 'mixed-magazine';
}

const PRESETS: Record<RevealStyleId, RevealStylePreset> = {
  ethereal_memory: {
    id: 'ethereal_memory',
    label: 'Ethereal Memory',
    backdropLayers: ['fog', 'dust', 'bokeh', 'lightStreaks', 'filmGrain'],
    buildupDurationMs: 7400,
    readingRhythm: 'word',
    pacing: 'slow',
    audioBedKey: 'piano_warm',
    particleDensity: 1.1,
    typography: 'serif-classic',
  },
  luxury_editorial: {
    id: 'luxury_editorial',
    label: 'Luxury Editorial',
    backdropLayers: ['fog', 'lightStreaks', 'filmGrain'],
    buildupDurationMs: 6200,
    readingRhythm: 'sentence',
    pacing: 'editorial',
    audioBedKey: 'strings_rise',
    particleDensity: 0.6,
    typography: 'serif-editorial',
  },
  emotional_film: {
    id: 'emotional_film',
    label: 'Emotional Film',
    backdropLayers: ['fog', 'volumetricRays', 'dust', 'filmGrain'],
    buildupDurationMs: 8200,
    readingRhythm: 'sentence',
    pacing: 'cinematic',
    audioBedKey: 'cinematic_air',
    particleDensity: 1.0,
    typography: 'serif-classic',
  },
  intimate_letter: {
    id: 'intimate_letter',
    label: 'Intimate Letter',
    backdropLayers: ['paperTexture', 'fog', 'ember', 'filmGrain'],
    buildupDurationMs: 6800,
    readingRhythm: 'word',
    pacing: 'intimate',
    audioBedKey: 'paper_room_tone',
    particleDensity: 0.7,
    hueShift: 8,
    typography: 'serif-handwritten',
  },
  modern_futuristic: {
    id: 'modern_futuristic',
    label: 'Modern Futuristic',
    backdropLayers: ['holographic', 'aurora', 'bokeh', 'lightStreaks'],
    buildupDurationMs: 5600,
    readingRhythm: 'paragraph',
    pacing: 'fluid',
    audioBedKey: 'heartbeat_low',
    particleDensity: 0.9,
    hueShift: -12,
    typography: 'sans-modern',
  },
};

export function getRevealStyle(id: RevealStyleId): RevealStylePreset {
  return PRESETS[id];
}

export function allRevealStyles(): RevealStylePreset[] {
  return Object.values(PRESETS);
}

/** Apply hue shift to a palette to introduce per-style variation without losing identity. */
export function tintPalette(palette: CinematicPalette, hueShift = 0): CinematicPalette {
  if (!hueShift) return palette;
  // Lightweight: we just return the palette as-is. The hue shift is a hint
  // backdrop layers can use via filter:hue-rotate when they composite.
  return palette;
}

export function pacingMultiplier(tier: PacingTier): number {
  switch (tier) {
    case 'slow': return 1.25;
    case 'cinematic': return 1.15;
    case 'editorial': return 1.0;
    case 'intimate': return 1.1;
    case 'fluid': return 0.85;
  }
}
